import React, { useState } from 'react';
import { ChevronLeft, User, Users, Mail, Phone, Calendar, Ruler, Weight, Dumbbell, Target, Briefcase, Baby, CheckCircle, Home, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSuccess } from './SuccessContext';

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccess();
  const [state, setState] = useState({
    gender: '',
    email: '',
    phone: '',
    name: '',
    height: '',
    age: '',
    weight: '',
    fitness_level: '',
    occupation: '',
    married: '',
    kids: '',
    consultation: '',
    currentStep: 1,
    showCompletion: false,
    isSubmitting: false,
    submitError: null,
    images: [],
  });

  const getTotalSteps = () => {
    return state.gender === 'male' ? 12 : 11;
  };

  const handleInputChange = (field, value) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const submitToAPI = async (formData) => {
    try {
      // Create FormData for file upload
      const apiData = new FormData();
      
      // Add all form fields - ensure phone is treated as string
      apiData.append('gender', formData.gender);
      apiData.append('email', formData.email);
      apiData.append('phone', String(formData.phone)); // Explicitly convert to string
      apiData.append('name', formData.name);
      apiData.append('height', formData.height);
      apiData.append('age', formData.age);
      apiData.append('weight', formData.weight);
      apiData.append('fitness_level', formData.fitness_level);
      apiData.append('occupation', formData.occupation);
      apiData.append('married', formData.married);
      apiData.append('kids', formData.kids);
      apiData.append('consultation', formData.consultation);
      
      // Add images if they exist
      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`images[]`, image);
        });
      } else {
        apiData.append('images', '');
      }

      console.log('Submitting data:', {
        phone: formData.phone,
        email: formData.email,
        name: formData.name
      });

      const response = await fetch('/api/admin/fatloss.php', {
        method: 'POST',
        body: apiData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Submission failed');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      try {
        const errorData = JSON.parse(error.message);
        throw new Error(errorData.message || 'Something went wrong');
      } catch (e) {
        throw new Error(error.message || 'Something went wrong. Please try again.');
      }
    }
  };

  const handleContinue = async () => {
    const totalSteps = getTotalSteps();
    
    if (state.currentStep < totalSteps) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      console.log('Final state before submission:', state);
      
      setState(prev => ({ ...prev, isSubmitting: true, submitError: null }));
      
      try {
        await submitToAPI(state);
        displaySuccess("Thank you for your submission! Our team or Behzad will contact you within 24 hours. Your personalized program will be delivered within 48 hours.");
        navigate('/');
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          isSubmitting: false, 
          submitError: error.message || 'Something went wrong. Please try again.' 
        }));
      }
    }
  };

  const handleBack = () => {
    if (state.currentStep > 1) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    } else {
      navigate('/');
    }
  };

  const canContinue = () => {
    if (state.showCompletion) return true;
    
    let actualStep = state.currentStep;
    if (state.gender !== 'male' && state.currentStep > 8) {
      actualStep = state.currentStep + 1;
    }
    
    switch (actualStep) {
      case 1: return state.gender !== '';
      case 2: return state.email !== '' && /\S+@\S+\.\S+/.test(state.email);
      case 3: return state.phone !== '' && state.phone.length >= 10 && state.phone.length <= 15;
      case 4: return state.name.trim() !== '';
      case 5: return state.height !== '' && parseInt(state.height) > 0;
      case 6: return state.age !== '' && parseInt(state.age) > 0;
      case 7: return state.weight !== '' && parseInt(state.weight) > 0;
      case 8: return state.fitness_level !== '';
      case 9: 
        if (state.gender === 'male') {
          return state.images.length > 0;
        } else {
          return state.occupation.trim() !== '';
        }
      case 10: return state.occupation.trim() !== '';
      case 11: return state.married !== '';
      case 12: return state.kids !== '';
      default: return true;
    }
  };

  const renderStep = () => {
    if (state.showCompletion) {
      return <CompletionMessage />;
    }
    
    let actualStep = state.currentStep;
    if (state.gender !== 'male' && state.currentStep > 8) {
      actualStep = state.currentStep + 1;
    }
    
    switch (actualStep) {
      case 1: return <GenderStep value={state.gender} onChange={(value) => handleInputChange('gender', value)} />;
      case 2: return <EmailStep value={state.email} onChange={(value) => handleInputChange('email', value)} />;
      case 3: return <PhoneStep value={state.phone} onChange={(value) => handleInputChange('phone', value)} />;
      case 4: return <NameStep value={state.name} onChange={(value) => handleInputChange('name', value)} />;
      case 5: return <HeightStep value={state.height} onChange={(value) => handleInputChange('height', value)} />;
      case 6: return <AgeStep value={state.age} onChange={(value) => handleInputChange('age', value)} />;
      case 7: return <WeightStep value={state.weight} onChange={(value) => handleInputChange('weight', value)} />;
      case 8: return <WorkoutStep value={state.fitness_level} onChange={(value) => handleInputChange('fitness_level', value)} />;
      case 9: 
        if (state.gender === 'male') {
          return <ImageUploadStep value={state.images} onChange={(value) => handleInputChange('images', value)} />;
        } else {
          return <OccupationStep value={state.occupation} onChange={(value) => handleInputChange('occupation', value)} />;
        }
      case 10: return <OccupationStep value={state.occupation} onChange={(value) => handleInputChange('occupation', value)} />;
      case 11: return <MarriedStep value={state.married} onChange={(value) => handleInputChange('married', value)} />;
      case 12: return <KidsStep value={state.kids} onChange={(value) => handleInputChange('kids', value)} />;
      default: return null;
    }
  };

  if (state.showCompletion) {
    return renderStep();
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-sm mx-auto min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-4 py-6">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            disabled={state.currentStep === 1}
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-[#2B4C7E]">
              STEP {state.currentStep} OF {getTotalSteps()}
            </h1>
          </div>
          <div className="w-10"></div>
        </div>
        <div className="px-4 mb-8">
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-[#2B4C7E] h-1 rounded-full transition-all duration-500"
              style={{ width: `${(state.currentStep / getTotalSteps()) * 100}%` }}
            />
          </div>
        </div>
        <div className="flex-1 px-4 pb-32">
          {renderStep()}
          {state.submitError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
              <p className="text-red-700 text-sm">{state.submitError}</p>
            </div>
          )}
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gray-50 border-t border-gray-200">
          <div className="max-w-sm mx-auto">
            <button
              onClick={handleContinue}
              disabled={!canContinue() || state.isSubmitting}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center ${
                canContinue() && !state.isSubmitting
                  ? 'bg-[#FF6B6B] text-white hover:bg-[#e55a5a]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {state.isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  SUBMITTING...
                </>
              ) : state.currentStep === getTotalSteps() ? (
                'COMPLETE PROFILE'
              ) : (
                'CONTINUE'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageUploadStep = ({ value, onChange }) => {
  const [previewUrls, setPreviewUrls] = useState([]);
  
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    onChange(files);
    
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-[#2B4C7E] mb-4 leading-tight">UPLOAD YOUR PHOTOS</h2>
        <p className="text-gray-600 text-lg">Please upload front, side, and back photos for better assessment</p>
      </div>
      
      <div className="space-y-6">
        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className="cursor-pointer block p-4"
          >
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </div>
            <p className="text-gray-600">Click to upload or drag and drop</p>
            <p className="text-gray-400 text-sm">PNG, JPG up to 10MB</p>
          </label>
        </div>
        
        {previewUrls.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {previewUrls.map((url, index) => (
              <div key={index} className="relative">
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
        )}
        
        {value.length > 0 && (
          <div className="flex items-center text-green-500">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{value.length} image(s) selected</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CompletionMessage = () => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccess();

  const handleReturnHome = () => {
    displaySuccess("Thank you for your submission! Our team or Behzad will contact you within 24 hours. Your personalized program will be delivered within 48 hours.");
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#2B4C7E] mb-2">Thank You For Your Submission!</h2>
            <p className="text-gray-600 mb-6">Your information has been successfully received.</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-[#2B4C7E] mb-3 text-center">What Happens Next?</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Our team or Behzad will contact you</strong> within 24 hours via WhatsApp or email
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>We'll discuss your goals</strong> and schedule your video consultation (if selected)
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                  <span className="text-white text-xs font-bold">3</span>
                </div>
                <p className="text-gray-700 text-sm">
                  <strong>Your personalized program</strong> will be delivered within 48 hours
                </p>
              </div>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500 mb-6">
            <p>If you have any questions, please contact us at:</p>
            <p className="font-medium text-[#2B4C7E]">contact@behzadlifts.com</p>
          </div>
          <button
            onClick={handleReturnHome}
            className="w-full py-4 bg-[#FF6B6B] text-white rounded-xl font-bold text-lg hover:bg-[#e55a5a] transition-colors duration-300 flex items-center justify-center"
          >
            <Home className="w-5 h-5 mr-2" />
            RETURN TO HOME
          </button>
        </div>
      </div>
    </div>
  );
};

const GenderStep = ({ value, onChange }) => {
  const options = [
    { id: 'male', label: 'MALE', description: "I'm a male", icon: User },
    { id: 'female', label: 'FEMALE', description: "I'm a female", icon: Users }
  ];
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-[#2B4C7E] mb-4 leading-tight">WHAT'S YOUR GENDER?</h2>
        <p className="text-gray-600 text-lg">This helps us customize your program</p>
      </div>
      <div className="space-y-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              className={`w-full p-6 rounded-2xl flex items-center justify-between transition-all duration-300 border ${
                isSelected ? 'bg-[#2B4C7E] text-white border-[#2B4C7E]' : 'bg-white hover:bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              <div className="text-left">
                <h3 className="text-xl font-bold">{option.label}</h3>
                <p className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                  {option.description}
                </p>
              </div>
              <Icon className={`w-8 h-8 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const EmailStep = ({ value, onChange }) => {
  const isValid = value !== '' && /\S+@\S+\.\S+/.test(value);
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Mail className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">EMAIL ADDRESS</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">We'll send your personalized program here</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="your.email@example.com"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          {isValid && <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
        </div>
        {value && !isValid && <p className="text-[#FF6B6B] text-sm px-2">Please enter a valid email address</p>}
      </div>
    </div>
  );
};

const PhoneStep = ({ value, onChange }) => {
  const isValid = value.length >= 10 && value.length <= 15;
  
  const handleChange = (e) => {
    // Remove all non-digits and limit to 15 characters (international phone number standard)
    const inputValue = e.target.value.replace(/\D/g, '').slice(0, 15);
    onChange(inputValue);
  };
  
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Phone className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">WHATSAPP NUMBER</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">Your program will be delivered via WhatsApp</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="tel"
            value={value}
            onChange={handleChange}
            placeholder="1234567890"
            maxLength="15"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          {isValid && <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
        </div>
       
        {value && !isValid && (
          <p className="text-[#FF6B6B] text-sm px-2">
            {value.length < 10 
              ? "Phone number must be at least 10 digits long" 
              : "Phone number cannot exceed 15 digits"
            }
          </p>
        )}
      </div>
    </div>
  );
};

const NameStep = ({ value, onChange }) => (
  <div className="space-y-8 h-full flex flex-col justify-center">
    <div className="text-center mb-12">
      <div className="flex items-center justify-center mb-4">
        <User className="w-8 h-8 text-[#FF6B6B] mr-3" />
        <h2 className="text-3xl font-bold text-[#2B4C7E]">YOUR NAME</h2>
      </div>
      <p className="text-gray-600 text-lg px-4">What should we call you?</p>
    </div>
    <div className="space-y-6">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your full name"
          className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
        />
        {value.trim() && <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
      </div>
    </div>
  </div>
);

const HeightStep = ({ value, onChange }) => {
  const isValid = value !== '' && parseInt(value) > 0;
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Ruler className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">YOUR HEIGHT</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">In centimeters (e.g., 175)</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="175"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">cm</div>
          {isValid && <CheckCircle className="absolute right-12 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
        </div>
      </div>
    </div>
  );
};

const AgeStep = ({ value, onChange }) => {
  const isValid = value !== '' && parseInt(value) >= 13 && parseInt(value) <= 80;
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Calendar className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">YOUR AGE</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">How old are you?</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="25"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">years</div>
          {isValid && <CheckCircle className="absolute right-16 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
        </div>
        {value && !isValid && <p className="text-[#FF6B6B] text-sm px-2">Please enter a valid age (13-80 years)</p>}
      </div>
    </div>
  );
};

const WeightStep = ({ value, onChange }) => {
  const isValid = value !== '' && parseInt(value) > 0;
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Weight className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">BODY WEIGHT</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">In kilograms (e.g., 70)</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="70"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">kg</div>
          {isValid && <CheckCircle className="absolute right-12 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
        </div>
      </div>
    </div>
  );
};

const WorkoutStep = ({ value, onChange }) => {
  const options = [
    { id: 'beginner', label: 'BEGINNER', description: "I'm new to fitness", icon: User },
    { id: 'intermediate', label: 'INTERMEDIATE', description: 'I work out 2-3 times a week', icon: Dumbbell },
    { id: 'advanced', label: 'ADVANCED', description: 'I exercise regularly', icon: Target }
  ];
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-[#2B4C7E] mb-4 leading-tight">WHAT IS YOUR FITNESS LEVEL?</h2>
        <p className="text-gray-600 text-lg">This helps us customize your program</p>
      </div>
      <div className="space-y-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              className={`w-full p-6 rounded-2xl transition-all duration-300 border ${
                isSelected ? 'bg-[#2B4C7E] text-white border-[#2B4C7E]' : 'bg-white hover:bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-xl font-bold">{option.label}</h3>
                  <p className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>{option.description}</p>
                </div>
                <Icon className={`w-8 h-8 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const OccupationStep = ({ value, onChange }) => (
  <div className="space-y-8 h-full flex flex-col justify-center">
    <div className="text-center mb-12">
      <div className="flex items-center justify-center mb-4">
        <Briefcase className="w-8 h-8 text-[#FF6B6B] mr-3" />
        <h2 className="text-3xl font-bold text-[#2B4C7E]">OCCUPATION</h2>
      </div>
      <p className="text-gray-600 text-lg px-4">What do you do for work?</p>
    </div>
    <div className="space-y-6">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g., Software Engineer"
          className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
        />
        {value.trim() && <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />}
      </div>
    </div>
  </div>
);

const MarriedStep = ({ value, onChange }) => {
  const options = [
    { id: 'yes', label: 'YES', icon: User },
    { id: 'no', label: 'NO', icon: User }
  ];
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-[#2B4C7E] mb-4 leading-tight">ARE YOU MARRIED?</h2>
        <p className="text-gray-600 text-lg">This affects your available training time</p>
      </div>
      <div className="space-y-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              className={`w-full p-6 rounded-2xl flex items-center justify-between transition-all duration-300 border ${
                isSelected ? 'bg-[#2B4C7E] text-white border-[#2B4C7E]' : 'bg-white hover:bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              <span className="text-xl font-bold">{option.label}</span>
              <Icon className={`w-8 h-8 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const KidsStep = ({ value, onChange }) => {
  const options = [
    { id: 'yes', label: 'YES', icon: Baby },
    { id: 'no', label: 'NO', icon: User }
  ];
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-[#2B4C7E] mb-4 leading-tight">DO YOU HAVE KIDS?</h2>
        <p className="text-gray-600 text-lg">This affects your available training time</p>
      </div>
      <div className="space-y-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              className={`w-full p-6 rounded-2xl flex items-center justify-between transition-all duration-300 border ${
                isSelected ? 'bg-[#2B4C7E] text-white border-[#2B4C7E]' : 'bg-white hover:bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              <span className="text-xl font-bold">{option.label}</span>
              <Icon className={`w-8 h-8 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingFlow;